"use client";

import { usePathname } from "next/navigation";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { CookieBanner } from "@/components/CookieBanner";

/**
 * Rendert Header, Footer und CookieBanner auf allen öffentlichen Seiten.
 *
 *  - Im Admin- und Dashboard-Bereich werden sie ausgeblendet, damit die
 *    dortigen Sidebars die komplette Höhe nutzen und das Layout nicht mit
 *    der öffentlichen Navigation kollidiert.
 *  - Auf der Wartungsseite gibt es absichtlich keine Navigation – sie
 *    soll als Standalone-Page erscheinen.
 *  - Auf Impressum, Datenschutz, AGB & Co. läuft das normale Chrome,
 *    auch im Wartungsmodus. So kann man von dort jederzeit zurück
 *    navigieren (Logo-Klick = /, der Wartungsmodus übernimmt dann selbst
 *    wieder).
 */
export function ConditionalChrome({
  children,
  visibleServiceSlugs,
  consentOpen = false,
}: {
  children: React.ReactNode;
  /** Öffentlich sichtbare Dienstleistungs-Slugs (vom Root-Layout geladen). */
  visibleServiceSlugs?: string[];
  /** true = noch keine Einwilligung (Server liest das Cookie) → Banner im HTML offen. */
  consentOpen?: boolean;
}) {
  const pathname = usePathname() || "/";

  const isAdminOrDashboard =
    pathname === "/admin" ||
    pathname.startsWith("/admin/") ||
    pathname === "/dashboard" ||
    pathname.startsWith("/dashboard/");

  const isMaintenancePage = pathname === "/wartung";

  // Akquise-One-Pager: standalone, bekommt eigene Mini-Chrome direkt
  // in der Seite. Nicht öffentlich verlinkt, nur per Direkt-Link.
  const isAcquisitionLanding = pathname.startsWith("/willkommen/");

  // Live-Umfrage und Vorab-Fragebogen der KI-Schulung: Teilnehmer kommen
  // per QR-Code bzw. Mail-Link aufs Handy – eigene Mini-Kopfzeile, keine
  // Navigation, die vom Mitmachen ablenkt.
  const isTrainingTool =
    pathname === "/live" || pathname.startsWith("/live/") || pathname.startsWith("/fragebogen/");

  const isChromeHidden =
    isAdminOrDashboard || isMaintenancePage || isAcquisitionLanding || isTrainingTool;

  if (isChromeHidden) {
    return <main id="main-content">{children}</main>;
  }

  return (
    <>
      <Header visibleServiceSlugs={visibleServiceSlugs} />
      <main id="main-content">{children}</main>
      <Footer visibleServiceSlugs={visibleServiceSlugs} />
      <CookieBanner initialOpen={consentOpen} />
    </>
  );
}
